import NextImage from 'next/image';
import Crown from 'svg/Crown';
import Location from 'svg/Location';
import ActivityBadge from 'components/ActivityBadge';
import AvatarStack from 'components/AvatarStack';
import { ButtonLink } from 'components/Button';
import DateColumn from 'components/DateColumn';
import { TopOpportunityEvent } from './types';

interface Props {
  event: TopOpportunityEvent;
}

export default function FeaturedOpportunityCard({ event }: Props) {
  // The venue reads on the pin line; a venue titled by its address would repeat it.
  const location = event.venueName || event.address;

  return (
    <article className="relative overflow-hidden rounded-3xl border border-color-border-input-lightmode bg-color-bg-lightmode-primary bg-[radial-gradient(90%_120%_at_55%_115%,rgba(255,124,31,0.2)_0%,rgba(255,190,143,0.1)_45%,rgba(255,255,255,0)_75%)] shadow-[inset_0px_4px_8px_0px_rgba(176,47,0,0.08)] dark:border-color-border-input-darkmode dark:bg-color-bg-darkmode-primary dark:bg-none max-lg:p-[0.5625rem] lg:p-ds-xl">
      <div className="flex items-stretch gap-ds-lg max-lg:flex-col">
        <div className="relative h-[21.25rem] min-w-0 flex-1 overflow-hidden rounded-[1.25rem] border border-color-border-input-lightmode dark:border-color-border-input-darkmode max-lg:h-[11.5625rem] max-lg:flex-none">
          <NextImage
            src={event.coverImageUrl}
            alt={event.title}
            fill
            className="object-cover object-center"
            loading="lazy"
          />
        </div>

        <div className="flex gap-ds-md lg:contents">
          <div className="flex shrink-0 flex-col items-center justify-between px-ds-md max-lg:w-10 max-lg:justify-start max-lg:gap-ds-sm max-lg:px-0 lg:w-[8.0625rem]">
            <DateColumn
              date={event.startDateTime}
              timezone={event.timezone}
              showWeekday
              widthClassName="sm:w-full"
              mobileVariant="rail"
            />
            <img
              src="/images/my-programs/fire.gif"
              alt=""
              className="h-20 w-20 object-contain max-lg:h-10 max-lg:w-10"
            />
          </div>

          <div className="w-px self-stretch bg-color-border-input-lightmode dark:bg-color-border-input-darkmode max-lg:hidden" />

          <div className="flex min-w-0 flex-1 flex-col justify-between gap-ds-md px-ds-md max-lg:px-0">
            <div className="flex flex-col gap-ds-xs">
              <div className="flex items-center justify-between gap-ds-sm">
                <ActivityBadge
                  type={event.type}
                  sport={event.sport}
                  isRfx={event.isRfx}
                  size="sm"
                />
                {event.priceDisplay && (
                  <span className="flex shrink-0 items-baseline gap-ds-sm whitespace-nowrap">
                    <span className="typography-product-text-card-desktop text-color-text-lightmode-placeholder dark:text-color-text-darkmode-placeholder max-lg:hidden">
                      from
                    </span>
                    <span className="max-lg:typography-product-heading-compact-mobile lg:typography-product-heading-compact-desktop text-color-text-lightmode-primary dark:text-color-text-darkmode-primary">
                      {event.priceDisplay}
                    </span>
                  </span>
                )}
              </div>

              <h3 className="max-lg:typography-product-display-mobile lg:typography-product-display-desktop text-color-text-lightmode-primary dark:text-color-text-darkmode-primary">
                {event.title}
              </h3>

              <div className="flex flex-col gap-ds-xs max-lg:gap-0">
                {location && (
                  <p className="max-lg:typography-product-text-card-mobile lg:typography-product-text-card-desktop flex items-center gap-ds-sm text-color-text-lightmode-secondary dark:text-color-text-darkmode-secondary">
                    <Location className="h-4 w-4 shrink-0 text-color-text-lightmode-icon dark:text-color-text-darkmode-icon" />
                    <span className="truncate">{location}</span>
                  </p>
                )}
                {/* The crown line is the organizer, mirroring the rows below the card. */}
                {event.organizerName && (
                  <p className="max-lg:typography-product-text-card-mobile lg:typography-product-text-card-desktop flex items-center gap-ds-sm text-color-text-lightmode-secondary dark:text-color-text-darkmode-secondary">
                    <Crown className="h-4 w-4 shrink-0 text-color-text-lightmode-icon dark:text-color-text-darkmode-icon" />
                    <span className="truncate">{event.organizerName}</span>
                  </p>
                )}
              </div>
            </div>

            {event.description && (
              <p className="typography-product-text-card-desktop line-clamp-2 text-color-text-lightmode-primary dark:text-color-text-darkmode-primary max-lg:hidden">
                {event.description}
              </p>
            )}

            <div className="flex flex-col gap-ds-md">
              <p className="max-lg:typography-product-text-card-mobile lg:typography-product-text-card-desktop flex items-center gap-ds-sm text-color-text-lightmode-secondary dark:text-color-text-darkmode-secondary">
                <span>Players going</span>
                <span>
                  {event.registrationCount}
                  {event.participantLimit ? `/${event.participantLimit}` : ''}
                </span>
              </p>
              <AvatarStack urls={event.avatarUrls} max={13} sizeClassName="size-6 lg:size-10" />
            </div>

            <ButtonLink href={event.href} variant="primary" size="md" className="w-full">
              Register
            </ButtonLink>
          </div>
        </div>
      </div>
    </article>
  );
}
